// `/c/{slug}/{mode}/…` (workspace-modes note §11.1). The company layout resolves access from
// `/v1/me`, provides the session + focus, and renders the company shell.
import {
  createRoute,
  notFound,
  Outlet,
  redirect,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { defaultMode } from "../extensions/modes";
import { ForcedEnrolment, mfaHold } from "../features/account/mfa";
import { useCompanyActivity } from "../features/chat/use-chat";
import { companyBySlug } from "../features/company";
import { useRecordRecentPlace } from "../features/home/recent-places";
import { featureState, SessionContext, useSession } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { useChangePings } from "../live/use-change-pings";
import { NotFoundPage } from "../pages/status-pages";
import { CompanyShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

const FOCUS_KEYS = ["s", "p", "scope", "thread"] as const;

export const companyRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "c/$slug",
  validateSearch: validateFocusSearch,
  beforeLoad: ({ context, params, location }) => {
    const company = companyBySlug(context.me, params.slug);
    if (!company) throw notFound();
    // A deep link with malformed focus keys is rewritten, so the URL always states the focus.
    // (Route search accumulates the raw parent search, so validate the raw location here.)
    const raw = location.search as Record<string, unknown>;
    const valid = validateFocusSearch(raw);
    if (FOCUS_KEYS.some((k) => k in raw && raw[k] !== valid[k])) {
      const qs = new URLSearchParams(valid as Record<string, string>).toString();
      throw redirect({
        href: qs ? `${location.pathname}?${qs}` : location.pathname,
        replace: true,
      });
    }
    return { company };
  },
  component: CompanyLayout,
  notFoundComponent: CompanyNotFound,
});

function CompanyLayout() {
  const { api } = companyRoute.useRouteContext();
  const { slug } = companyRoute.useParams();
  const { me } = useSession();
  // Recomputed from the live `/me`, so a role or entitlement change applies without a reload.
  const company = companyBySlug(me, slug);
  // RT-01: every screen of the company refetches what it shows when another session writes.
  useChangePings(api, company?.id ?? "");
  // CHAT-SWITCH-A: active / away in every mode, for presence and `@here`.
  useCompanyActivity(
    api,
    company?.id ?? "",
    company ? featureState(me, company.id, "chat").enabled : false,
  );
  // Home's "Recent": the places opened in this company, in this browser.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.searchStr });
  useRecordRecentPlace(company?.id ?? "", pathname, search);
  if (!company) return <CompanyNotFound />;
  // HARDEN-mfa: the company requires two-step sign-in of its owners and admins, and this one
  // has not set it up: the enrolment screen instead of the company (the API refuses it anyway).
  const hold = mfaHold(me, company.id);
  if (hold) return <ForcedEnrolment api={api} me={me} reason={hold} companyName={company.name} />;
  return (
    <SessionContext.Provider value={{ me, company }}>
      <CompanyShell me={me} api={api} company={company}>
        <Outlet />
      </CompanyShell>
    </SessionContext.Provider>
  );
}

function CompanyNotFound() {
  const { me } = useSession();
  const { api } = authedRoute.useRouteContext();
  const params = useParams({ strict: false }) as { slug?: string };
  return (
    <PlainShell me={me} api={api} current={null}>
      <NotFoundPage
        message={`No company "${params.slug ?? ""}", or you do not have access to it.`}
      />
    </PlainShell>
  );
}

/** `/c/{slug}` lands on the role's default mode, keeping the focus query. */
export const companyIndexRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/",
  beforeLoad: ({ context, params, search }) => {
    const mode = defaultMode(context.me, context.company);
    if (mode) throw redirect({ to: mode.path, params, search, replace: true });
  },
  component: function NoModes() {
    return (
      <NotFoundPage message="No mode of this company is available to you yet. Ask an admin to enable an add-on." />
    );
  },
});
